import { useCallback } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';

import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import {
	useConvertDialogActions,
	useConvertDialogState,
	type ConvertDialogStateValue,
} from './ConvertDialogStore.ts';
import { countSyncBlocks } from './countSyncBlocks.ts';
import { filterToMarkdownSafeAdf } from './filterToMarkdownSafeAdf.ts';
import { isMarkdownConversionLossy } from './isMarkdownConversionLossy.ts';
import { useIsMarkdownMode } from './useIsMarkdownMode.ts';
import { useMarkdownConvertFlags } from './useMarkdownConvertFlags.tsx';
import { useUpdateMarkdownMode } from './useUpdateMarkdownMode.ts';

/**
 * Mirrors `EditorObjectHeaderContext.editorActions`: either the real
 * `EditorActions` class (when the editor is mounted) or a minimal
 * placeholder with just `isDocumentEmpty` (when it isn't).
 */
type DefaultNoop = () => void;
type EmptyEditorActions = {
	isDocumentEmpty: DefaultNoop;
};
export type EditorActionsOrEmpty = EditorActions | EmptyEditorActions;

/** Optional subset of `FlagsStateContainer` we call. `unknown` returns let void-returning stubs satisfy the type. */
export type FlagsLike = {
	[K in 'showErrorFlag' | 'showFlag' | 'hideFlag']?: (
		...args: Parameters<FlagsStateContainer[K]>
	) => unknown;
};

type UseMarkdownModeActionsArgs = {
	contentId: string;
	isLivePage: boolean;
	editorActions: EditorActionsOrEmpty;
	flags: FlagsLike;
};

export type UseMarkdownModeActionsResult = {
	isMarkdownMode: boolean;
	isUpdating: boolean;
	/** Current dialog state (open + direction + lossy) for the conversion confirmation modal. */
	convertDialogState: ConvertDialogStateValue;
	/** Close the conversion confirmation modal. */
	closeConvertDialog: () => void;
	/** True when the doc contains synced blocks and the convert flow is blocked on the user unlinking them first. */
	isSyncBlockBlockerOpen: boolean;
	/** Number of `bodiedSyncBlock` nodes the most recent convert attempt found — drives the pluralised dialog copy. */
	syncBlockCount: number;
	/** Close the unlink-synced-blocks blocker dialog. */
	closeSyncBlockBlocker: () => void;
	/**
	 * Entry-point for "Convert to markdown". Inspects the doc and opens
	 * either the unlink-synced-blocks blocker (if the doc contains any
	 * `bodiedSyncBlock`) or the confirmation dialog with the right
	 * `isLossy` value. Never flips the BE flag directly — that happens
	 * on dialog confirm.
	 */
	convertToMarkdown: () => Promise<void>;
	/**
	 * Entry-point for "Convert to rich text". Always opens the confirmation
	 * dialog (no lossy state — the markdown→rich direction is never lossy).
	 */
	convertToRichText: () => Promise<void>;
	/** Called from the dialog when user confirms a `toMarkdown` conversion. */
	confirmConvertToMarkdown: () => Promise<{ success: boolean }>;
	/** Called from the dialog when user confirms a `toRichText` conversion. */
	confirmConvertToRichText: () => Promise<{ success: boolean }>;
};

/**
 * Encapsulates the markdown-mode toggle flow for a live page:
 *  - reads current mode + mutation loading state,
 *  - drains in-flight collab steps before flipping the BE flag (avoids
 *    NCS-restore-replay duplication, see `drainUnconfirmedSteps`),
 *  - opens the unified confirmation dialog for both directions (always —
 *    user-confirmed conversion is now the contract for both `toMarkdown`
 *    and `toRichText`, lossy or not),
 *  - on dialog-confirm, optionally rewrites the doc with the markdown-safe
 *    filter (lossy→markdown only) so the user immediately sees what will
 *    be persisted.
 */
export const useMarkdownModeActions = ({
	contentId,
	isLivePage,
	editorActions,
	flags,
}: UseMarkdownModeActionsArgs): UseMarkdownModeActionsResult => {
	const { updateMarkdownMode, loading: isUpdating } = useUpdateMarkdownMode();
	const isMarkdownMode = useIsMarkdownMode(isLivePage ? contentId : undefined);
	// Dialog open/close lives in a sweet-state store so multiple call sites
	// (header overflow menu, title `.md`-suffix gesture, `cmd+shift+x`
	// shortcut) all drive the same dialog instance rendered by `MoreActions`.
	const [{ dialog, syncBlockCount }] = useConvertDialogState();
	const isSyncBlockBlockerOpen = syncBlockCount > 0;
	const { openConvertDialog, closeConvertDialog, openSyncBlockBlocker, closeSyncBlockBlocker } =
		useConvertDialogActions();
	const convertFlags = useMarkdownConvertFlags(flags);

	// Wait for any unconfirmed collab steps to be acked before flipping the
	// markdown-mode flag. The convert mutation invalidates the live-doc
	// session on the BE, which triggers an NCS restore that would otherwise
	// re-apply our unconfirmed steps on top of the server doc that already
	// has them — duplicating anything typed just before the click.
	const drainUnconfirmedSteps = useCallback(async () => {
		if (!('getResolvedEditorState' in editorActions)) {
			return;
		}
		try {
			await editorActions.getResolvedEditorState('publish');
		} catch {
			// Swallow — don't block convert if the drain fails.
		}
	}, [editorActions]);

	const flipFlagToMarkdown = useCallback(async () => {
		await drainUnconfirmedSteps();
		const result = await updateMarkdownMode(contentId, true);
		if (!result.success) {
			convertFlags.showError('toMarkdown');
			return { success: false };
		}
		convertFlags.showSuccess('toMarkdown');
		return { success: true };
	}, [contentId, convertFlags, drainUnconfirmedSteps, updateMarkdownMode]);

	const flipFlagToRichText = useCallback(async () => {
		await drainUnconfirmedSteps();
		const result = await updateMarkdownMode(contentId, false);
		if (!result.success) {
			convertFlags.showError('toRichText');
			return { success: false };
		}
		convertFlags.showSuccess('toRichText');
		return { success: true };
	}, [contentId, convertFlags, drainUnconfirmedSteps, updateMarkdownMode]);

	// Lossy path: after the BE flag flip, strip nodes/marks that won't
	// survive markdown round-trip and push the filtered ADF back into the
	// editor so the user sees the final state immediately.
	//
	// Gated behind `cc-disable-be-md-conversion` — the BE now owns the
	// rewrite, and this FE filter only runs when the BE conversion is
	// killed via that gate.
	const confirmConvertToMarkdown = useCallback(async () => {
		const result = await flipFlagToMarkdown();
		if (!result.success) {
			return result;
		}

		if (
			'replaceDocument' in editorActions &&
			'getValue' in editorActions &&
			fg('cc-disable-be-md-conversion')
		) {
			try {
				const currentAdf = await editorActions.getValue();
				const filteredAdf = filterToMarkdownSafeAdf(currentAdf);
				if (filteredAdf) {
					editorActions.replaceDocument(filteredAdf);
				}
			} catch {
				// Editor may already be unmounted; the flag flip succeeded so
				// the next mount will load the rewritten doc from the BE.
			}
		}

		return { success: true };
	}, [editorActions, flipFlagToMarkdown]);

	const confirmConvertToRichText = useCallback(async () => {
		return flipFlagToRichText();
	}, [flipFlagToRichText]);

	// Entry point for the "Convert to markdown" menu item / shortcut /
	// `.md` title gesture. Inspect the doc:
	//   1. If it contains any `bodiedSyncBlock` node — show the unlink-first
	//      blocker. Synced blocks fan out to other pages and have no
	//      markdown representation, so we don't expose a "convert anyway"
	//      action — the user must unlink them in the editor.
	//   2. Otherwise open the confirmation dialog with the right `isLossy`
	//      value. The flag flip happens later from `confirmConvertToMarkdown`.
	const convertToMarkdown = useCallback(async () => {
		let adf: unknown;
		if ('getValue' in editorActions) {
			try {
				adf = await editorActions.getValue();
			} catch {
				adf = undefined;
			}
		}

		const syncBlockNodeCount = countSyncBlocks(adf);
		if (syncBlockNodeCount > 0) {
			openSyncBlockBlocker(syncBlockNodeCount);
			return;
		}

		openConvertDialog({
			direction: 'toMarkdown',
			isLossy: isMarkdownConversionLossy(adf),
		});
	}, [editorActions, openConvertDialog, openSyncBlockBlocker]);

	const convertToRichText = useCallback(async () => {
		openConvertDialog({ direction: 'toRichText' });
	}, [openConvertDialog]);

	return {
		isMarkdownMode,
		isUpdating,
		convertDialogState: dialog,
		closeConvertDialog,
		isSyncBlockBlockerOpen,
		syncBlockCount,
		closeSyncBlockBlocker,
		convertToMarkdown,
		convertToRichText,
		confirmConvertToMarkdown,
		confirmConvertToRichText,
	};
};
